import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Typography } from "@platform/design-system";

import type { FinancialsGridContext, FinancialsRow } from "../types";

export const CoverageHeaderRow = ({
  row,
  context,
}: {
  row: FinancialsRow;
  context: FinancialsGridContext;
}): React.JSX.Element => {
  const coverageId = row.coverageId ?? "";
  const coverage = context.coverageById.get(coverageId);
  const name = coverage?.name ?? "";
  const blockNames = row.blockNames;

  const isOrphaned = blockNames === undefined || blockNames.length === 0;
  const isMultiBlock = blockNames !== undefined && blockNames.length > 1;
  const chipLabel = isOrphaned
    ? "Omitted from Totals"
    : isMultiBlock
      ? "Included in Multiple Totals"
      : `Rolls up to ${blockNames?.[0] ?? "Totals"}`;
  const chipColor = isOrphaned ? "warning" : "success";

  const expanded = !context.collapsedCoverages.has(coverageId);

  const handleToggle = (): void => {
    if (coverageId) {
      context.toggleCoverageExpanded(coverageId);
    }
  };

  return (
    <Box
      data-testid={`coverage-header-${coverageId}`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "100%",
        width: "100%",
        px: 1,
        cursor: "pointer",
      }}
      onClick={handleToggle}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small">
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        {name}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          label={chipLabel}
          size="small"
          color={chipColor}
          variant="filled"
          sx={{
            fontWeight: 400,
            fontSize: "0.75rem",
            borderRadius: "9999px",
          }}
        />
        {isMultiBlock && (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption">
                  <strong>Totals Blocks</strong>
                </Typography>
                {blockNames.map((blockName) => (
                  <Typography variant="caption" key={blockName}>
                    {blockName}
                  </Typography>
                ))}
              </Box>
            }
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "text.secondary", cursor: "pointer" }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
